import { createContext, useContext } from "react";
import { IngredientListColumns } from "./useIngredientColumns";
import { IngredientList } from "src/api/client-api/ingredients/useGetAllIngredients";
import { GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { DelayedSearch } from "../../../utils/useDelayedSearch";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { AppSearch } from "src/api/_common/hydra/search";
import { ApiPagination } from "src/api/_common/params/useApiPaginationUrlSync";
import type { TableAction } from "src/components/Grid/headers/GridTableActions";

/**
 * NOTE — This file is intentionally separated from `IngredientListContext.tsx`
 * (which defines the `IngredientListContextProvider` component) to satisfy the
 * Vite React Fast Refresh "consistent components exports" rule. Mixing a hook
 * (`useIngredientListContext`) with a component export in the same module makes
 * Fast Refresh unable to hot-replace the file: on HMR, a stale provider stays
 * mounted while the new hook references a brand-new context instance, causing
 * `useContext` to return `null` and the runtime check below to throw
 * "useIngredientListContext must be used within an IngredientListContextProvider".
 *
 * See https://github.com/vitejs/vite-plugin-react-swc#consistent-components-exports
 */
export type IngredientListContextInitialValues = {
    mrt_columns: IngredientListColumns;
    columnConfiguration: GridColumnsType<IngredientList>;

    totalItems: number;
    ingredients: IngredientList[];
    isRefetching: boolean;
    isReady: boolean;

    menuItems: MenuItemType<IngredientList>[];
    tableActions: TableAction[];
    globalSearch: DelayedSearch;
    gridKey: string;
    appSearch: AppSearch | undefined;
    pagination: ApiPagination;
};

export const IngredientListContext = createContext<IngredientListContextInitialValues | null>(null);

export const useIngredientListContext = () => {
    const context = useContext(IngredientListContext);

    if (context) {
        return context;
    }

    throw new Error(`useIngredientListContext must be used within an IngredientListContextProvider`);
};
